import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TDropdownTagSelectorOption } from 'libs/design-system/src/types/dropdown-tag-selector';
import {
  DropdownTagSelector,
  iso3ToIso2,
} from '@unified-checkout/design-system';

import type {
  ICountryDetails,
  IFields,
} from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import { COUNTRIES_LIST } from '@app/dashboard/constants/countries-list';
import { SINGLE_PAYMENTS_FIELDS } from '@app/dashboard/constants/payment-links/single-payments';
import { useLangStore } from '@app/dashboard/store/lang';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import LazyImage from '@app/dashboard/components/shared/LazyImage';

import styles from './countries-dropdown.module.scss';

const CountriesDropDown = ({ fields, setFields }: IFields) => {
  const { t } = useTranslation();

  const { lang } = useLangStore();

  const savedCountry =
    getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY) || COUNTRIES.EGY;
  const merchantCountry = iso3ToIso2(savedCountry).toUpperCase();

  const dropdownCountriesList = useMemo(() => {
    return COUNTRIES_LIST.map((country) => ({
      id: country.name,
      label: country.name,
      value: country,
      searchKey: country.name,
    }));
  }, []);

  const selectedCountries = useMemo(
    () =>
      fields[SINGLE_PAYMENTS_FIELDS.ALLOWED_COUNTRIES]?.value || ([] as any),
    [fields]
  );

  const renderCountry = useCallback((country: ICountryDetails) => {
    const icon =
      typeof country.icon === 'string' ? (
        <LazyImage
          className={styles['countries-dropdown__country-item-icon']}
          src={country.icon}
          alt={country.name}
        />
      ) : (
        country.icon
      );

    return (
      <div className={styles['countries-dropdown__country-item']}>
        {icon}
        <span className={styles['countries-dropdown__country-item-name']}>
          {country.name}
        </span>
      </div>
    );
  }, []);

  useEffect(() => {
    const defaultCountry = COUNTRIES_LIST.find(
      (country) => country.iso === merchantCountry
    );

    if (!defaultCountry) return;

    setFields((prev: any) => {
      const updatedFields = { ...prev };
      updatedFields[SINGLE_PAYMENTS_FIELDS.ALLOWED_COUNTRIES] = {
        value: [
          {
            id: defaultCountry.name,
            label: defaultCountry.name,
            value: defaultCountry,
            searchKey: defaultCountry.name,
          },
        ],
      };

      return updatedFields;
    });
  }, []);

  return (
    <DropdownTagSelector
      dropdownMenuProps={{
        lang,
        verticalPlacement: 'bottom',
      }}
      renderDropdownMenuItem={(item) =>
        renderCountry(item.value as ICountryDetails)
      }
      optionsList={dropdownCountriesList}
      selectedOptions={selectedCountries}
      tagsInputProps={{
        name: 'allowed-countries',
        placeholder: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.placeholders.selectCountry'
        ),
        getTagShapeProps: (option: TDropdownTagSelectorOption) => ({
          type: 'country',
          id: option.id,
          text: option.label,
          country: option.value?.icon,
          name: option.value?.name,
        }),
      }}
      onSelectionChange={(items) => {
        setFields((prev: any) => {
          return {
            ...prev,
            [SINGLE_PAYMENTS_FIELDS.ALLOWED_COUNTRIES]: {
              value: items,
            },
          };
        });
      }}
    />
  );
};

export default memo(CountriesDropDown);
